import {
  Button,
  ButtonVariant,
  Grid,
  Paper,
  Select,
  SelectVariant,
  Text,
  TextSize,
  TextWeight,
  theme
} from '@focaldata/cin-ui-components'
import React from 'react'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import {
  MarketVisibilityFilter,
  visibleInAllMarkets
} from '../../Questionnaire/MarketVisibility/marketVisibility'
import { AutoTranslateProgress } from '../hooks/useAutoTranslate'
import { computeCompletion } from '../lib/completion'
import { CustomAudienceTranslationGroup } from '../lib/extractAudienceFields'

interface Props {
  targetLanguage: string
  declaredLanguages: ReadonlyArray<string>
  primaryLanguage: string
  // Country-supported languages from useAudienceCountries — used to scope
  // the "+ Add language" dropdown so researchers can only add languages
  // that the survey's selected country (and panel supplier) actually
  // supports. For Canada this would be [{code:'en'…}, {code:'fr'…}];
  // since `en` is already the default the dropdown ends up showing just
  // French.
  availableLanguages: ReadonlyArray<{ code: string; name: string }>
  entries: ReadonlyArray<DraftQuestionnaireEntry>
  audienceGroups: ReadonlyArray<CustomAudienceTranslationGroup>
  // Each language's own market scope: a question no market fielded in that
  // language shows is not part of its denominator. Defaults to counting
  // everything (single-survey usage).
  visibilityFor?: (languageCode: string) => MarketVisibilityFilter
  onSelectLanguage: (code: string) => void
  onAddLanguage: (code: string) => void
  onRemoveLanguage: (code: string) => void
  onAutoTranslate: () => Promise<void>
  onOpenImport: () => void
  autoTranslateProgress: AutoTranslateProgress
}

const TranslationsSidebar: React.FC<Props> = ({
  targetLanguage,
  declaredLanguages,
  primaryLanguage,
  availableLanguages,
  entries,
  audienceGroups,
  visibilityFor,
  onSelectLanguage,
  onAddLanguage,
  onRemoveLanguage,
  onAutoTranslate,
  onOpenImport,
  autoTranslateProgress
}) => {
  const { running, total, done, failed, errors } = autoTranslateProgress

  // The picker only shows languages the country's panel supplier actually
  // supports, minus the primary (already implicit) and anything the user
  // has already declared. For Canada (en, fr) with English as primary,
  // this collapses to a single option: French.
  const availableToAdd = availableLanguages
    .filter(
      (l) => l.code !== primaryLanguage && !declaredLanguages.includes(l.code)
    )
    .map((l) => ({
      id: l.code,
      name: l.name || getLanguageLabel(l.code),
      value: l.code
    }))

  return (
    <Paper>
      <Grid container direction="column" spacing={2} style={{ padding: 16 }}>
        <Grid item>
          <Text size={TextSize.s} weight={TextWeight.SemiBold}>
            LANGUAGES
          </Text>
        </Grid>

        {declaredLanguages.length === 0 ? (
          <Grid item>
            <Text size={TextSize.s}>
              No additional languages yet. Select a language below to start
              translating.
            </Text>
          </Grid>
        ) : (
          declaredLanguages.map((code) => {
            const isActive = code === targetLanguage
            const isVisible = visibilityFor?.(code) ?? visibleInAllMarkets
            const stats = computeCompletion(
              entries.filter((entry) => isVisible(entry.hiddenInMarkets)),
              code,
              audienceGroups,
              isVisible
            )
            return (
              <Grid item key={code}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectLanguage(code)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectLanguage(code)
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: isActive
                      ? theme.palette.primary.light
                      : 'transparent',
                    border: `1px solid ${
                      isActive ? theme.palette.primary.light : 'transparent'
                    }`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Text
                    size={TextSize.m}
                    weight={isActive ? TextWeight.SemiBold : TextWeight.Regular}
                  >
                    {getLanguageLabel(code)}
                  </Text>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Text size={TextSize.s}>{stats.pct}%</Text>
                    <button
                      type="button"
                      aria-label={`Remove ${getLanguageLabel(code)}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemoveLanguage(code)
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: theme.palette.text.secondary,
                        fontSize: 16,
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </span>
                </div>
              </Grid>
            )
          })
        )}

        {/* Only render the picker when there's something to add — an empty
            Select would otherwise show a stray blank row. The `key` (declared
            count) remounts the Select after each add so it returns to the
            "+ Add language" placeholder instead of holding the just-added,
            now-removed option (the control treats optionValue as initial). */}
        {availableToAdd.length > 0 && (
          <Grid item>
            <Select
              key={`add-language-${declaredLanguages.length}`}
              variant={SelectVariant.Outlined}
              options={availableToAdd}
              optionValue=""
              placeholder="+ Add language"
              fullWidth
              onChange={(e) => {
                const code = e.target.value
                if (!code) return
                onAddLanguage(code)
                onSelectLanguage(code)
              }}
            />
          </Grid>
        )}

        <Grid item>
          <div
            style={{
              height: 1,
              background: theme.palette.divider,
              margin: '4px 0'
            }}
          />
        </Grid>

        <Grid item>
          <Button
            variant={ButtonVariant.PrimaryFilled}
            onClick={onAutoTranslate}
            disabled={running || declaredLanguages.length === 0}
            fullWidth
          >
            {running
              ? `Auto-translating ${done}/${total}…`
              : 'Auto-translate ALL'}
          </Button>
        </Grid>
        {/* Show error feedback after auto-translate completes with failures.
            The alert is positioned immediately after the button so it's visible
            without scrolling. Uses error.main border + error.light background to
            stand out while remaining consistent with the design system. */}
        {!running && failed > 0 && (
          <Grid item>
            <div
              role="alert"
              style={{
                padding: 12,
                borderRadius: 8,
                background: theme.palette.error.light,
                borderLeft: `4px solid ${theme.palette.error.main}`
              }}
            >
              <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                {done > 0
                  ? `⚠ ${done} saved, ${failed} failed. Retry to fill only the remaining empty fields.`
                  : `⚠ Nothing was saved (${failed} failed). Retry when the issue is resolved.`}
              </Text>
              {errors.map((error, index) => (
                <Text key={`${index}-${error}`} size={TextSize.s}>
                  {error}
                </Text>
              ))}
            </div>
          </Grid>
        )}
        <Grid item>
          <Button
            variant={ButtonVariant.SecondaryOutlined}
            onClick={onOpenImport}
            // Disable until at least one language is declared. The dialog
            // saves the dropped .docx as the currently-active language's
            // translation; with no declared language there's nowhere to
            // write to. (Also disabled while auto-translate is running so
            // researchers don't race the two flows against each other.)
            disabled={running || declaredLanguages.length === 0}
            fullWidth
          >
            {targetLanguage
              ? `Import .docx for ${getLanguageLabel(targetLanguage)}`
              : 'Import .docx'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default TranslationsSidebar
